// app/(app)/invoices/add.tsx
import { Button } from "@/components/default/forms";
import SearchableSelect from "@/components/default/forms/SearchableSelect";
import { useAppTheme } from "@/hooks/useAppTheme";
import { inventoryApi } from "@/services/inventoryApi";
import { Area, locationApi } from "@/services/locationApi";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { fetchCustomers } from "@/store/slices/customerSlice";
import { createDraftInvoice } from "@/store/slices/invoiceSlice";
import { fetchProducts } from "@/store/slices/productSlice";
import {
  clearSelectedArea,
  getSelectedArea,
  saveSelectedArea,
} from "@/utils/areaStorage";
import { Ionicons } from "@expo/vector-icons";
import { router, useFocusEffect, useNavigation } from "expo-router";
import { useCallback, useEffect, useLayoutEffect, useState } from "react";
import {
  Alert,
  KeyboardAvoidingView,
  Platform,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  TouchableOpacity,
  View,
} from "react-native";

interface LineItem {
  id: string;
  productId: string;
  productName: string;
  quantity: number;
  unitPrice: number;
  total: number;
  maxStock: number;
}

export default function AddInvoiceScreen() {
  const { colors, spacing } = useAppTheme();
  const navigation = useNavigation();
  const dispatch = useAppDispatch();
  const { products } = useAppSelector((state) => state.products);
  const { customers } = useAppSelector((state) => state.customers);
  const { updating, error } = useAppSelector((state) => state.invoices);

  const [areas, setAreas] = useState<Area[]>([]);
  const [filteredAreas, setFilteredAreas] = useState<Area[]>([]);
  const [selectedAreaId, setSelectedAreaId] = useState("");
  const [loadingAreas, setLoadingAreas] = useState(false);

  const [selectedCustomerId, setSelectedCustomerId] = useState("");
  const [selectedCustomer, setSelectedCustomer] = useState<any>(null);
  const [lineItems, setLineItems] = useState<LineItem[]>([]);
  const [errors, setErrors] = useState({
    area: "",
    customer: "",
    lineItems: "",
  });
  const [stockMap, setStockMap] = useState<Record<string, number>>({});

  const [areaSearch, setAreaSearch] = useState("");
  const [customerSearch, setCustomerSearch] = useState("");
  const [productSearch, setProductSearch] = useState("");
  const [loadingCustomers, setLoadingCustomers] = useState(false);
  const [loadingProducts, setLoadingProducts] = useState(false);

  // Load areas and restore saved area on mount
  useEffect(() => {
    const init = async () => {
      // First load areas
      await loadAreas();

      // Then try to restore saved area
      const savedAreaId = await getSelectedArea();
      if (savedAreaId && areas.some((a) => a.id.toString() === savedAreaId)) {
        setSelectedAreaId(savedAreaId);
        // Fetch customers for this area
        dispatch(
          fetchCustomers({
            page: 1,
            limit: 100,
            search: "",
            areaId: parseInt(savedAreaId),
          }),
        );
      } else if (savedAreaId) {
        // Saved area is invalid – clear it
        await clearSelectedArea();
      }
      // Fetch products
      dispatch(fetchProducts({ page: 1, limit: 100, search: "" }));
    };
    init();
  }, []);

  const loadAreas = async () => {
    setLoadingAreas(true);
    try {
      const fetchedAreas = await locationApi.getAreas();
      setAreas(fetchedAreas);
      setFilteredAreas(fetchedAreas);
    } catch (err) {
      console.error(err);
      Alert.alert("Error", "Failed to load areas");
    } finally {
      setLoadingAreas(false);
    }
  };

  useEffect(() => {
    if (areaSearch.trim() === "") {
      setFilteredAreas(areas);
    } else {
      const lowerSearch = areaSearch.toLowerCase();
      const filtered = areas.filter((area) =>
        area.name.toLowerCase().includes(lowerSearch),
      );
      setFilteredAreas(filtered);
    }
  }, [areaSearch, areas]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      setLoadingProducts(true);
      dispatch(
        fetchProducts({ page: 1, limit: 20, search: productSearch }),
      ).finally(() => setLoadingProducts(false));
    }, 500);
    return () => clearTimeout(timeout);
  }, [productSearch, dispatch]);

  useFocusEffect(
    useCallback(() => {
      // Reset only customer, line items, and other fields; keep area
      setSelectedCustomerId("");
      setSelectedCustomer(null);
      setLineItems([]);
      setErrors({ area: "", customer: "", lineItems: "" });
      setStockMap({});
      setProductSearch("");
    }, []),
  );

  const calculateSubtotal = () => {
    return lineItems.reduce((sum, item) => sum + (item.total || 0), 0);
  };

  useLayoutEffect(() => {
    navigation.setOptions({
      headerRight: () => (
        <View style={{ marginRight: spacing.md }}>
          <Text
            style={{ fontSize: 16, fontWeight: "700", color: colors.primary }}
          >
            Total Amount: ₹{calculateSubtotal().toFixed(2)}
          </Text>
        </View>
      ),
    });
  }, [navigation, calculateSubtotal, colors, spacing]);

  useEffect(() => {
    if (error) Alert.alert("Error", error);
  }, [error]);

  useEffect(() => {
    if (selectedAreaId) {
      // Save selected area whenever it changes
      saveSelectedArea(selectedAreaId);
      dispatch(
        fetchCustomers({
          page: 1,
          limit: 100,
          search: "",
          areaId: parseInt(selectedAreaId),
        }),
      );
      setSelectedCustomerId("");
      setSelectedCustomer(null);
    } else {
      // If area is cleared, remove from storage
      clearSelectedArea();
    }
  }, [selectedAreaId, dispatch]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      if (selectedAreaId) {
        setLoadingCustomers(true);
        dispatch(
          fetchCustomers({
            page: 1,
            limit: 20,
            search: customerSearch,
            areaId: parseInt(selectedAreaId),
          }),
        ).finally(() => setLoadingCustomers(false));
      }
    }, 500);
    return () => clearTimeout(timeout);
  }, [customerSearch, selectedAreaId]);

  const fetchStockForProduct = async (productId: string) => {
    if (stockMap[productId]) return stockMap[productId];
    try {
      const balance = await inventoryApi.getStockByProduct(parseInt(productId));
      const stock = balance.currentStock;
      setStockMap((prev) => ({ ...prev, [productId]: stock }));
      return stock;
    } catch (err) {
      console.error(err);
      return 0;
    }
  };

  const addLineItem = () => {
    const newItem: LineItem = {
      id: Date.now().toString(),
      productId: "",
      productName: "",
      quantity: 1,
      unitPrice: 0,
      total: 0,
      maxStock: 0,
    };
    setLineItems((prev) => [newItem, ...prev]);
  };

  const updateLineItem = async (
    index: number,
    field: keyof LineItem,
    value: any,
  ) => {
    const updated = [...lineItems];
    const item = { ...updated[index] };

    if (field === "productId") {
      const productIdNum = parseInt(value, 10);
      const product = products.find((p) => p.id === productIdNum);
      if (product) {
        item.productId = product.id.toString();
        item.productName = product.name;
        item.unitPrice = product.price;
        const stock = await fetchStockForProduct(product.id.toString());
        item.maxStock = stock;
        item.quantity = stock > 0 ? 1 : 0;
      }
    } else if (field === "quantity") {
      let qty = parseInt(value, 10) || 0;
      if (qty > item.maxStock && item.maxStock > 0) {
        Alert.alert("Stock Limit", `Only ${item.maxStock} packs available`);
        qty = item.maxStock;
      }
      item.quantity = qty;
    } else if (field === "unitPrice") {
      let price = parseFloat(value) || 0;
      item.unitPrice = price;
    }

    item.total = item.quantity * item.unitPrice;
    updated[index] = item;
    setLineItems(updated);
  };

  const incrementQuantity = (index: number) => {
    const item = lineItems[index];
    const newQty = item.quantity + 1;
    if (newQty <= item.maxStock || item.maxStock === 0) {
      updateLineItem(index, "quantity", newQty.toString());
    } else {
      Alert.alert("Stock Limit", `Only ${item.maxStock} packs available`);
    }
  };

  const decrementQuantity = (index: number) => {
    const item = lineItems[index];
    if (item.quantity > 1) {
      updateLineItem(index, "quantity", (item.quantity - 1).toString());
    }
  };

  const incrementUnitPrice = (index: number) => {
    const item = lineItems[index];
    const oldPrice = item.unitPrice.toString();
    const newPrice = parseFloat(oldPrice) + 1;
    updateLineItem(index, "unitPrice", newPrice.toFixed(2).toString());
  };

  const decrementUnitPrice = (index: number) => {
    const item = lineItems[index];
    const oldPrice = item.unitPrice.toString();
    if (parseFloat(oldPrice) >= 1) {
      const newPrice = parseFloat(oldPrice) - 1;
      updateLineItem(index, "unitPrice", newPrice.toFixed(2).toString());
    }
  };

  const removeLineItem = (index: number) => {
    setLineItems((prev) => prev.filter((_, i) => i !== index));
  };

  const validate = () => {
    let valid = true;
    const newErrors = { area: "", customer: "", lineItems: "" };
    if (!selectedAreaId) {
      newErrors.area = "Select an area/route";
      valid = false;
    }
    if (!selectedCustomerId) {
      newErrors.customer = "Select a customer";
      valid = false;
    }
    if (lineItems.length === 0) {
      newErrors.lineItems = "At least one product is required";
      valid = false;
    }
    for (let i = 0; i < lineItems.length; i++) {
      const item = lineItems[i];
      if (!item.productId) {
        newErrors.lineItems = `Row ${i + 1}: Select a product`;
        valid = false;
        break;
      }
      if (!item.quantity || item.quantity <= 0) {
        newErrors.lineItems = `Row ${i + 1}: Quantity > 0`;
        valid = false;
        break;
      }
      if (item.quantity > item.maxStock && item.maxStock > 0) {
        newErrors.lineItems = `Row ${i + 1}: Only ${item.maxStock} in stock`;
        valid = false;
        break;
      }
      if (item.unitPrice <= 0) {
        newErrors.lineItems = `Row ${i + 1}: Unit price must be > 0`;
        valid = false;
        break;
      }
    }
    setErrors(newErrors);
    return valid;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    const items = lineItems.map((item) => ({
      productId: parseInt(item.productId),
      quantity: item.quantity,
      unitPrice: item.unitPrice,
      discountPercent: 0,
    }));
    const result = await dispatch(
      createDraftInvoice({
        customerId: parseInt(selectedCustomerId),
        invoiceDate: new Date().toISOString().split("T")[0],
        discount: 0,
        items,
      }),
    );
    if (createDraftInvoice.fulfilled.match(result)) {
      Alert.alert(
        "Draft Created",
        "Invoice saved as draft. You can confirm it later.",
        [
          {
            text: "OK",
            onPress: () =>
              router.push(`/(app)/invoices/${result.payload?.invoiceId}`),
          },
        ],
      );
    }
  };

  const usedProductIds = lineItems
    .map((item) => item.productId)
    .filter((id) => id !== "");

  const styles = StyleSheet.create({
    container: { flex: 1, backgroundColor: colors.background },
    keyboardAvoid: { flex: 1 },
    scrollContent: { padding: spacing.lg, paddingBottom: 100 },
    sectionTitle: {
      fontSize: 18,
      fontWeight: "600",
      color: colors.text,
      marginTop: spacing.md,
      marginBottom: spacing.sm,
    },
    inputContainer: { marginBottom: spacing.lg },
    duesContainer: {
      flexDirection: "row",
      justifyContent: "space-between",
      alignItems: "center",
      backgroundColor: colors.primary + "10",
      padding: spacing.md,
      borderRadius: 8,
      marginBottom: spacing.lg,
    },
    duesLabel: { fontSize: 14, color: colors.textLight },
    duesAmount: { fontSize: 16, fontWeight: "700", color: colors.primary },
    itemCard: {
      backgroundColor: colors.background,
      borderRadius: 12,
      padding: spacing.md,
      marginBottom: spacing.md,
      borderWidth: 1,
      borderColor: colors.border,
    },
    itemHeader: {
      flexDirection: "row",
      justifyContent: "space-between",
      alignItems: "center",
      marginBottom: spacing.sm,
    },
    itemLeftInfo: {
      flexDirection: "row",
      alignItems: "center",
      gap: spacing.sm,
    },
    totalStockText: { fontSize: 12, color: colors.textLight },
    itemRemove: { padding: spacing.xs },
    quantityRow: {
      flexDirection: "row",
      alignItems: "center",
      gap: spacing.sm,
      marginBottom: spacing.md,
    },
    quantityLabel: { flex: 1, fontSize: 14, color: colors.text },
    quantityControl: {
      flexDirection: "row",
      alignItems: "center",
      gap: spacing.sm,
    },
    qtyButtonMinus: {
      width: 36,
      height: 36,
      borderRadius: 5,
      backgroundColor: colors.border,
      justifyContent: "center",
      alignItems: "center",
    },
    qtyButtonPlus: {
      width: 44,
      height: 44,
      borderRadius: 5,
      backgroundColor: colors.primary,
      justifyContent: "center",
      alignItems: "center",
    },
    qtyInput: {
      width: 60,
      textAlign: "center",
      borderWidth: 1,
      borderColor: colors.border,
      borderRadius: 8,
      paddingVertical: 6,
      fontSize: 16,
      fontWeight: "500",
      color: colors.text,
    },
    priceRow: {
      flexDirection: "row",
      alignItems: "center",
      gap: spacing.sm,
    },
    priceControl: {
      flexDirection: "row",
      alignItems: "center",
      gap: spacing.sm,
    },
    priceButton: {
      width: 32,
      height: 32,
      backgroundColor: colors.border,
      justifyContent: "center",
      alignItems: "center",
    },
    priceInput: {
      width: 80,
      textAlign: "center",
      borderWidth: 1,
      borderColor: colors.border,
      borderRadius: 8,
      paddingVertical: 6,
      fontSize: 14,
      color: colors.text,
    },
    footer: {
      backgroundColor: colors.background,
      borderTopWidth: 1,
      borderTopColor: colors.border,
      padding: spacing.lg,
      paddingBottom: spacing.lg + (Platform.OS === "ios" ? 20 : 40),
    },
    footerRow: {
      flexDirection: "row",
      alignItems: "center",
      gap: spacing.md,
    },
    createButton: { flex: 1 },
    addProductButton: {
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "center",
      backgroundColor: colors.primary,
      paddingHorizontal: spacing.md,
      paddingVertical: spacing.sm,
      borderRadius: 8,
      gap: spacing.xs,
      elevation: 2,
      shadowColor: "#000",
      shadowOffset: { width: 0, height: 1 },
      shadowOpacity: 0.2,
      shadowRadius: 2,
    },
    addProductText: {
      color: "#FFF",
      fontWeight: "600",
      fontSize: 14,
    },
  });

  return (
    <View style={styles.container}>
      <KeyboardAvoidingView
        behavior={Platform.OS === "ios" ? "padding" : "height"}
        style={styles.keyboardAvoid}
      >
        <ScrollView
          contentContainerStyle={styles.scrollContent}
          keyboardShouldPersistTaps="handled"
        >
          <SearchableSelect
            label="Area / Route"
            placeholder="Search area..."
            options={filteredAreas.map((a) => ({
              label: a.name,
              value: a.id.toString(),
            }))}
            value={selectedAreaId}
            onValueChange={(val) => setSelectedAreaId(val)}
            onSearch={setAreaSearch}
            loading={loadingAreas}
            error={errors.area}
            containerStyle={styles.inputContainer}
          />

          <SearchableSelect
            label="Customer"
            placeholder="Search by name or phone"
            options={customers.map((c) => ({
              label: `${c.fullName} (${c.phone}) - ${c.address}`,
              value: c.id.toString(),
            }))}
            value={selectedCustomerId}
            onValueChange={(val, label) => {
              setSelectedCustomerId(val);
              const cust = customers.find((c) => c.id.toString() === val);
              setSelectedCustomer(cust);
            }}
            searchValue={customerSearch}
            onSearchChange={setCustomerSearch}
            loading={loadingCustomers}
            error={errors.customer}
            containerStyle={styles.inputContainer}
            disabled={!selectedAreaId}
          />

          {selectedCustomer && (
            <View style={styles.duesContainer}>
              <Text style={styles.duesLabel}>Total Dues</Text>
              <Text style={styles.duesAmount}>
                ₹
                {(() => {
                  const balance = Number(selectedCustomer.balance);
                  return isNaN(balance) ? "0.00" : balance.toFixed(2);
                })()}
              </Text>
            </View>
          )}

          <Text style={styles.sectionTitle}>Products</Text>
          {errors.lineItems && (
            <Text
              style={{
                color: colors.danger,
                fontSize: 12,
                marginBottom: spacing.md,
              }}
            >
              {errors.lineItems}
            </Text>
          )}
          {lineItems.map((item, idx) => {
            const otherUsedIds = usedProductIds.filter(
              (id) => id !== item.productId,
            );
            const filteredProducts = products.filter(
              (p) => !otherUsedIds.includes(p.id.toString()),
            );

            const productOptions = filteredProducts.map((p) => ({
              label: `${p.name} (₹${p.price})`,
              value: p.id.toString(),
            }));

            const finalOptions = (() => {
              if (
                item.productId &&
                !productOptions.some((opt) => opt.value === item.productId)
              ) {
                return [
                  {
                    label: item.productName || `Product ${item.productId}`,
                    value: item.productId,
                  },
                  ...productOptions,
                ];
              }
              return productOptions;
            })();

            return (
              <View key={item.id} style={styles.itemCard}>
                <View style={styles.itemHeader}>
                  <View style={styles.itemLeftInfo}>
                    <Text style={{ fontWeight: "500", color: colors.text }}>
                      Item {lineItems.length - idx}
                    </Text>
                    {item.maxStock > 0 && (
                      <Text style={styles.totalStockText}>
                        Stock: {item.maxStock}
                      </Text>
                    )}
                    <Text style={styles.totalStockText}>
                      Total: ₹{item.total.toFixed(2)}
                    </Text>
                  </View>
                  <TouchableOpacity
                    onPress={() => removeLineItem(idx)}
                    style={styles.itemRemove}
                  >
                    <Ionicons
                      name="trash-outline"
                      size={20}
                      color={colors.danger}
                    />
                  </TouchableOpacity>
                </View>

                <SearchableSelect
                  label="Product"
                  placeholder="Select product"
                  options={finalOptions}
                  value={item.productId}
                  onValueChange={(val) => updateLineItem(idx, "productId", val)}
                  containerStyle={{ marginBottom: spacing.md }}
                  searchValue={productSearch}
                  onSearchChange={setProductSearch}
                  loading={loadingProducts}
                />

                <View style={styles.quantityRow}>
                  <Text style={styles.quantityLabel}>Quantity</Text>
                  <View style={styles.quantityControl}>
                    <TouchableOpacity
                      style={styles.qtyButtonMinus}
                      onPress={() => decrementQuantity(idx)}
                    >
                      <Ionicons name="remove" size={20} color={colors.text} />
                    </TouchableOpacity>
                    <TextInput
                      style={styles.qtyInput}
                      value={item.quantity.toString()}
                      onChangeText={(val) =>
                        updateLineItem(idx, "quantity", val)
                      }
                      keyboardType="number-pad"
                    />
                    <TouchableOpacity
                      style={styles.qtyButtonPlus}
                      onPress={() => incrementQuantity(idx)}
                    >
                      <Ionicons name="add" size={24} color="#FFF" />
                    </TouchableOpacity>
                  </View>
                </View>

                <View style={styles.priceRow}>
                  <Text style={styles.quantityLabel}>Unit Price (₹)</Text>
                  <View style={styles.priceControl}>
                    <TouchableOpacity
                      style={styles.priceButton}
                      onPress={() => decrementUnitPrice(idx)}
                    >
                      <Ionicons name="remove" size={16} color={colors.text} />
                    </TouchableOpacity>
                    <TextInput
                      style={styles.priceInput}
                      value={item.unitPrice.toString()}
                      onChangeText={(val) =>
                        updateLineItem(idx, "unitPrice", val)
                      }
                      keyboardType="numeric"
                    />
                    <TouchableOpacity
                      style={styles.priceButton}
                      onPress={() => incrementUnitPrice(idx)}
                    >
                      <Ionicons name="add" size={16} color={colors.text} />
                    </TouchableOpacity>
                  </View>
                </View>
              </View>
            );
          })}
        </ScrollView>
      </KeyboardAvoidingView>

      <View style={styles.footer}>
        <View style={styles.footerRow}>
          <Button
            title="Create Invoice (Draft)"
            onPress={handleSubmit}
            loading={updating}
            containerStyle={styles.createButton}
          />
          <TouchableOpacity
            style={styles.addProductButton}
            onPress={addLineItem}
          >
            <Ionicons name="add" size={20} color="#FFF" />
            <Text style={styles.addProductText}>Product</Text>
          </TouchableOpacity>
        </View>
      </View>
    </View>
  );
}
